import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Box, 
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import { useAuth } from 'contexts/AuthContext';

const LoginPage: React.FC = () => {
  const { auth, login } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState<string | null>(null);
  
  // Check if this is a callback from Auth0
  const isCallback = location.search.includes('callback=true');
  
  // Extract error information from URL if present
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const error = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');
    
    if (error) {
      // Format and set error message
      const errorMessage = errorDescription 
        ? `${error}: ${decodeURIComponent(errorDescription)}` 
        : error;
      setAuthError(errorMessage);
      
      // Remove error params from URL without refresh
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [location.search]);
  
  useEffect(() => {
    // If user is already authenticated and this is not a callback page, redirect to saved path
    if (auth.isAuthenticated && !isCallback && !authError) {
      // Get the saved return path or default to accounts page
      const returnTo = sessionStorage.getItem('returnTo') || '/accounts';
      // Clear the saved path to avoid stale redirects
      sessionStorage.removeItem('returnTo');
      navigate(returnTo);
    }
  }, [auth.isAuthenticated, isCallback, authError, navigate]);
  
  // If this is a callback with no error, show loading spinner while Auth0 processes the authentication
  if (isCallback && !authError && auth.isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          padding: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 4 }}>
          Completing authentication...
        </Typography>
      </Box>
    );
  }

  const handleLogin = () => {
    setAuthError(null);
    login();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        padding: 2
      }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 4,
          width: '100%',
          maxWidth: 400,
          borderRadius: 2,
          textAlign: 'center'
        }}
      >
        <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
          Welcome!!
        </Typography>
        
        {authError && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            Authentication Error: {authError}
          </Alert>
        )}
        
        {auth.error && !authError && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            {auth.error}
          </Alert>
        )}
        
        <Typography variant="body1" sx={{ mb: 4 }}>
          Please log in to access your account. We use Auth0 for secure authentication.
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          onClick={handleLogin}
          disabled={auth.isLoading}
        >
          {auth.isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Log in with Auth0'
          )}
        </Button>
      </Paper>
    </Box>
  );
};

export default LoginPage;